import { useEffect, useState } from 'react';
import {
  APPROVAL_STAGE_LABEL,
  MAX_REMINDER_DAYS,
  type ApprovalStage,
  type ReminderSettings,
} from '@jfh/intake-shared';
import { api } from '../lib/api.js';
import { Icon } from './Icon.js';
import { useToast } from './Toast.js';

/**
 * Settings → Teams & approvals → Approval reminders: how many working days an
 * application may wait at each stage before its one reminder goes out.
 */

const STAGES: { key: ApprovalStage; blurb: string }[] = [
  { key: 'cs', blurb: 'Trade applications, and stage 1 of credit applications' },
  { key: 'accounts', blurb: 'The credit decision (stage 2), counted from Customer Service’s approval' },
  { key: 'supplier', blurb: 'Supplier account applications' },
];

export function ApprovalReminders() {
  const toast = useToast();
  const [saved, setSaved] = useState<ReminderSettings | null>(null);
  const [draft, setDraft] = useState<ReminderSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .getReminders()
      .then((r) => {
        setSaved(r.settings);
        setDraft(r.settings);
      })
      .catch(() => undefined);
  }, []);

  if (!draft || !saved) {
    return (
      <div className="card" style={{ padding: 'var(--pad-card)', color: 'var(--text-3)' }}>
        Loading approval reminders…
      </div>
    );
  }

  const valid = Object.values(draft.days).every((d) => Number.isInteger(d) && d >= 1 && d <= MAX_REMINDER_DAYS);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.putReminders(draft);
      setSaved(r.settings);
      setDraft(r.settings);
      toast({ title: 'Approval reminders saved', icon: 'check' });
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 'var(--pad-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon name="clock" size={18} />
        <strong style={{ fontSize: 14.5 }}>Approval reminders</strong>
        <span className={`badge ${saved.enabled ? 'approved' : 'neutral'}`}>{saved.enabled ? 'On' : 'Off'}</span>
      </div>
      <p className="field-help" style={{ marginTop: 6 }}>
        When an application has waited longer than its stage’s limit, it’s marked <strong>Overdue</strong> in
        the inbox, its approval card is posted again in that stage’s Teams channel, its Hub item turns red and
        the admins get one email. One reminder per stage. Working days are Monday to Friday, skipping England
        &amp; Wales bank holidays; reminders only go out 08:00–18:00.
      </p>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13.5, fontWeight: 600 }}>
        <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
        Send reminders
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14, opacity: draft.enabled ? 1 : 0.55 }}>
        {STAGES.map((s) => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{APPROVAL_STAGE_LABEL[s.key]}</div>
              <div className="field-help">{s.blurb}</div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                className="input"
                type="number"
                min={1}
                max={MAX_REMINDER_DAYS}
                step={1}
                value={Number.isNaN(draft.days[s.key]) ? '' : draft.days[s.key]}
                disabled={!draft.enabled}
                onChange={(e) => setDraft({ ...draft, days: { ...draft.days, [s.key]: e.target.valueAsNumber } })}
                style={{ width: 76 }}
                aria-label={`${APPROVAL_STAGE_LABEL[s.key]} limit in working days`}
              />
              working days
            </label>
          </div>
        ))}
      </div>
      {!valid && (
        <div style={{ color: 'var(--st-declined)', fontSize: 12.5, marginTop: 8 }}>
          Each limit must be a whole number of working days from 1 to {MAX_REMINDER_DAYS}.
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <button className="btn primary" onClick={() => void save()} disabled={saving || !dirty || !valid}>
          {saving ? 'Saving…' : 'Save reminders'}
        </button>
      </div>
    </div>
  );
}
